import type { ApiAccount } from './api.js';

/**
 * The code a new account is about to get — as a **preview**, not a decision.
 *
 * The decision is `next_account_code()` in migration 0037, and the server makes
 * it: `POST /api/accounts` refuses a code in the body outright. This function
 * exists so the form can show what the code will be while somebody is still
 * choosing the parent, which is the one moment it is useful and the one moment
 * the server has not been asked yet.
 *
 * ── The scheme ──────────────────────────────────────────────────────────────
 *
 *     1              Assets                       level 1
 *     1-1            Current Assets               level 2
 *     1-1-1          Cash in Hand                 level 3
 *     1-1-1-1        Cash in Hand — Head Office   level 4
 *     1-1-3-1-0001   CUS-0001 — Sadiq Poultry     level 5
 *
 * One segment per level, so the code *is* the path, and a parent may hold as many
 * children as the business has. That is the whole reason it replaced the
 * four-digit positional scheme (`1000` / `1100` / `1130` / `1131`), which allowed
 * **nine** children per account and no more — a book with ten banks or ten
 * utility expenses had to start inventing codes off the grid, and the old seed
 * already did (`1153` under `1140`, `2125` under `2100`).
 *
 * **Level 5 keeps its padded series**, because that level is the sub-ledger: one
 * account per customer, supplier and salesperson, minted by
 * `ensureSubLedgerAccount` rather than designed, where a deleted party must never
 * hand its code to the next one and four hundred rows have to sort.
 *
 * ── Why this can disagree with the server, and why that is fine ──────────────
 *
 * It reads the chart the browser last fetched. A second person adding a sibling
 * in the meantime makes this preview one behind, and the code that comes back
 * from the write is the real one — which is why the form displays this read-only
 * and the screen takes `result.code` from the response rather than assuming.
 */

/** Level 5 — the sub-ledger — is `<parent>-0001`. */
const SEQUENCE_WIDTH = 4;

/** The deepest level the chart holds; `accounts_depth_ck` allows five. */
const MAX_LEVEL = 5;

/**
 * The next free number among a parent's children.
 *
 * Continues from the highest already issued rather than filling the first gap: a
 * code once used belongs to whoever had it, even after the account is gone, and
 * `accounts_code_uq` has no `deleted_at` filter — so a gap in the numbers is
 * usually a deletion rather than a space left deliberately. This is the same rule
 * `next_account_code` applies, and the two agree segment for segment.
 */
function nextNumber(parentCode: string, taken: ReadonlySet<string>): number {
  const prefix = `${parentCode}-`;
  let highest = 0;
  for (const code of taken) {
    if (!code.startsWith(prefix)) continue;
    const tail = code.slice(prefix.length);
    // Only a direct child. `1-1-2` and `1-1-2-1` share a prefix with `1-1`, and
    // counting the grandchild would skip a number for no reason.
    if (tail.includes('-')) continue;
    if (!/^\d+$/.test(tail)) continue;
    highest = Math.max(highest, Number(tail));
  }
  return highest + 1;
}

/**
 * Suggest the code for a new account under `parent` (undefined means top level).
 *
 * Returns an empty string when the parent is already at the deepest level, which
 * is the case the form uses to disable the New-under control rather than offer a
 * code the server would refuse.
 */
export function nextAccountCode(
  parent: ApiAccount | undefined,
  accounts: readonly ApiAccount[],
): string {
  const taken = new Set(accounts.map((account) => account.code));

  // Top level: 1 Assets … 6 Operating Expenses, then 7 for whatever a book adds.
  if (!parent) {
    let next = 1;
    while (taken.has(String(next))) next += 1;
    return String(next);
  }

  if (parent.level >= MAX_LEVEL) return '';

  const level = parent.level + 1;
  const n = nextNumber(parent.code, taken);
  return level >= MAX_LEVEL
    ? `${parent.code}-${String(n).padStart(SEQUENCE_WIDTH, '0')}`
    : `${parent.code}-${n}`;
}
